'use client';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { useEffect, useState } from 'react';
import { createClient } from '@/lib/supabase/client';

export function QueryProvider({ children }: { children: React.ReactNode }) {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 5 * 60 * 1000,    // 5 min – global default (reduced API calls)
            gcTime: 15 * 60 * 1000,       // 15 min – garbage collect unused
            retry: 1,
            refetchOnWindowFocus: false,   // Disabled: was causing query floods on tab switch
            refetchOnReconnect: false,     // Disabled: was causing cascading refetches on network blips
          },
        },
      })
  );

  // Only an identity change invalidates cached data. A token refresh is not an
  // identity change: refetching every mounted query on TOKEN_REFRESHED closed a
  // loop where one 401 forced a refresh, the refresh refetched ~10 queries, one
  // of those 401'd again, and the cycle ran every 130 ms until GoTrue answered
  // 429. One user produced 437 token requests and 26 sign-ins this way on
  // 2026-08-28. USER_UPDATED carries the same session and is equally inert here;
  // AuthContext already refreshes the profile for it.
  useEffect(() => {
    const supabase = createClient();
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_IN' || event === 'SIGNED_OUT') {
        queryClient.invalidateQueries({ refetchType: 'active' });
      }
    });
    return () => subscription.unsubscribe();
  }, [queryClient]);

  return (
    <QueryClientProvider client={queryClient}>
      {children}
      {process.env.NODE_ENV === 'development' && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  );
}
